import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box, VStack, Text, Icon, Tooltip, TooltipContent, TooltipText,
} from '@gluestack-ui/themed';
import {
  Trophy, Award, Medal, AlertCircle,
} from 'lucide-react-native';

type GradeLevel = 'error' | 'info' | 'success';

interface GradeCardProps {
  grade: number | string;
  size?: 'sm' | 'md' | 'lg';
  showIcon?: boolean;
  showLabel?: boolean;
  compact?: boolean; // Minimal mode: no border, tighter spacing
  tooltip?: string;
}

const GradeCard: React.FC<GradeCardProps> = ({
  grade,
  size = 'md',
  showIcon = true,
  showLabel = true,
  compact = false,
  tooltip,
}) => {
  const { t } = useTranslation();

  // Get grade achievement level - German system (1.0 best, 4.0 minimum pass, >4.0 fail)
  const getAchievementLevel = (gradeVal: number) => {
    if (gradeVal > 4.0) return { label: t('student.grading.levels.failed'), icon: AlertCircle, color: 'error' as GradeLevel };
    if (gradeVal >= 3.5) return { label: t('student.grading.levels.sufficient'), icon: Medal, color: 'info' as GradeLevel };
    if (gradeVal >= 2.5) return { label: t('student.grading.levels.satisfactory'), icon: Medal, color: 'info' as GradeLevel };
    if (gradeVal >= 1.5) return { label: t('student.grading.levels.good'), icon: Award, color: 'success' as GradeLevel };
    return { label: t('student.grading.levels.very_good'), icon: Trophy, color: 'success' as GradeLevel };
  };

  const gradeNumber = typeof grade === 'string' ? parseFloat(grade) : grade;
  const achievement = getAchievementLevel(gradeNumber);

  // Size configurations
  const sizeConfig: Record<string, {
    cardSize: number;
    padding: '$1' | '$2' | '$3' | '$4' | '$5';
    iconSize: 'xs' | 'sm' | 'md';
    iconPadding: '$1' | '$2' | '$3';
    fontSize: number;
    lineHeight: number;
    labelSize: number;
  }> = {
    sm: {
      cardSize: compact ? 60 : 80,
      padding: compact ? '$1' : '$2',
      iconSize: 'xs',
      iconPadding: '$1',
      fontSize: compact ? 20 : 18,
      lineHeight: compact ? 22 : 20,
      labelSize: 8,
    },
    md: {
      cardSize: compact ? 80 : 120,
      padding: compact ? '$2' : '$4',
      iconSize: 'sm',
      iconPadding: '$2',
      fontSize: compact ? 24 : 28,
      lineHeight: compact ? 26 : 32,
      labelSize: 10,
    },
    lg: {
      cardSize: compact ? 100 : 135,
      padding: compact ? '$3' : '$5',
      iconSize: 'md',
      iconPadding: '$3',
      fontSize: compact ? 30 : 36,
      lineHeight: compact ? 32 : 40,
      labelSize: 12,
    },
  };

  const config = sizeConfig[size];

  // Get color tokens safely
  const getBorderColor = () => {
    switch (achievement.color) {
      case 'error':
        return '$error700';
      case 'info':
        return '$info700';
      case 'success':
        return '$success700';
      default:
        return '$success700';
    }
  };

  const getBgColor = () => {
    switch (achievement.color) {
      case 'error':
        return '$error100';
      case 'info':
        return '$info100';
      case 'success':
        return '$success100';
      default:
        return '$success100';
    }
  };

  const getTextColor = () => {
    switch (achievement.color) {
      case 'error':
        return '$error700';
      case 'info':
        return '$info700';
      case 'success':
        return '$success700';
      default:
        return '$success700';
    }
  };

  const CardContent = (
    <Box
      borderWidth={compact ? 0 : 2}
      borderColor={getBorderColor()}
      borderRadius="$lg"
      p={config.padding}
      minWidth={config.cardSize}
      maxWidth={config.cardSize}
      bg={compact ? 'transparent' : undefined}
    >
      <VStack alignItems="center" space="xs">
        {showIcon && (
          <Box bg={getBgColor()} p={config.iconPadding} borderRadius="$full" mb={compact ? '$0' : '$1'}>
            <Icon as={achievement.icon} size={config.iconSize} color={getTextColor()} />
          </Box>
        )}

        <Text fontSize={config.fontSize} fontWeight="bold" lineHeight={config.lineHeight} color={getTextColor()}>
          {gradeNumber.toFixed(1)}
        </Text>

        {showLabel && !compact && (
          <Text
            fontSize={config.labelSize}
            color="$textSubtle"
            textTransform="uppercase"
            letterSpacing={1}
            textAlign="center"
          >
            {achievement.label}
          </Text>
        )}
      </VStack>
    </Box>
  );

  // If tooltip is provided, wrap with Tooltip component using v1 API
  if (tooltip) {
    return (
      <Tooltip
        trigger={(triggerProps: Record<string, unknown>) => React.cloneElement(CardContent, triggerProps)}
      >
        <TooltipContent>
          <TooltipText>{tooltip}</TooltipText>
        </TooltipContent>
      </Tooltip>
    );
  }

  return CardContent;
};

export default GradeCard;
